import cn from "classnames"

import { Flex } from "@/atoms"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { FileTextOutlined } from "@/icons"
import { DismissedBy } from "@/molecules/DismissedTag"
import { LineClamp } from "@/molecules/LineClamp"

import styles from "./StatusBar.module.scss"

import type { StatusChange } from "@/domain/issue"

export const StatusChangeRow = (props: {
  change: StatusChange
  id: string
}) => {
  const { change, id } = props
  const { from, to, at, by, method, reason } = change
  const { t } = useTranslation()
  const { t: tEntity } = useTranslation("translation", {
    keyPrefix: "dismissedEntity"
  })
  const date = getDateInUserLocale(getDateFromSeconds(at), DEFAULT_LONG_FORMAT)

  return (
    <Flex
      vertical
      gap={6}
      id={id}
      className={cn(styles.indented, styles.change)}
      data-id="issue-status-change"
    >
      <Flex wrap align="center" gap={6}>
        <span className={styles.step}>
          {t("issue.statusBar.step", {
            from: t(`general.${from}`),
            to: t(`general.${to}`)
          })}
        </span>
        {date && <span className={styles.changeDate}>{date}</span>}
        <span className={styles.changeBy}>·</span>
        <span className={cn(styles.actorLine, styles.changeBy)}>
          <Trans
            i18nKey="issue.statusBar.by"
            components={{
              actor: (
                <span className={styles.actor}>
                  <DismissedBy
                    dismissedBy={by}
                    dismissMethod={method}
                    t={tEntity}
                  />
                </span>
              )
            }}
          />
        </span>
      </Flex>
      {reason && (
        <Flex gap={6} align="flex-start" className={styles.reason}>
          <FileTextOutlined aria-hidden className={styles.reasonIcon} />
          <div className={styles.reasonBody}>
            <LineClamp clamp={3} classes={{ container: styles.reasonText }}>
              {tEntity(`reasons.${reason}`, { defaultValue: reason })}
            </LineClamp>
          </div>
        </Flex>
      )}
    </Flex>
  )
}
